import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  CollectorListItem,
  CollectorsConfig
} from '../../../domain/collector-profile.model';
import { AlertService } from '../../../services/alert.service';
import { getStateClass } from '../collector-state-class.util';

interface DisplayEventSkill {
  label: string;
  codes: string[];
}

const EVENT_SKILL_VALUES = ['EXPERT', 'HIGH', 'MEDIUM', 'LOW', 'NONE'] as const;
const INCONSISTENT_SKILL_VALUE = '__INCONSISTENT__' as const;

type EventSkillValue = (typeof EVENT_SKILL_VALUES)[number];
type DisplayEventSkillValue =
  | EventSkillValue
  | typeof INCONSISTENT_SKILL_VALUE;

@Component({
  selector: 'app-collector-event-skills-card',
  templateUrl: './collector-event-skills-card.component.html',
  styleUrls: [
    '../collector-card.shared.css',
    './collector-event-skills-card.component.css'
  ]
})
export class CollectorEventSkillsCardComponent implements OnChanges {
  @Input() collector!: CollectorListItem;
  @Input() config!: CollectorsConfig;
  @Input() readOnly = false;
  @Output() change = new EventEmitter<void>();

  readonly inconsistentSkillValue = INCONSISTENT_SKILL_VALUE;

  displayEventSkills: DisplayEventSkill[] = [];

  private lastInconsistentAlertKey: string | null = null;

  constructor(private alertService: AlertService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.config) {
      this.displayEventSkills = this.buildDisplayEventSkills();
    }

    if (changes.collector || changes.config) {
      this.warnOnInconsistentMergedSkills();
    }
  }

  emitChange(): void {
    if (!this.readOnly) {
      this.change.emit();
    }
  }

  getEventSkillValue(displaySkill: DisplayEventSkill): DisplayEventSkillValue {
    const values = this.getSkillValues(displaySkill.codes);
    return values.length <= 1 ? values[0] ?? 'NONE' : INCONSISTENT_SKILL_VALUE;
  }

  getEventSkillClass(displaySkill: DisplayEventSkill): string | null {
    return this.isInconsistent(displaySkill)
      ? 'state-inconsistent'
      : getStateClass(this.getEventSkillValue(displaySkill));
  }

  isInconsistent(displaySkill: DisplayEventSkill): boolean {
    return this.getSkillValues(displaySkill.codes).length > 1;
  }

  onEventSkillChanged(
    displaySkill: DisplayEventSkill,
    value: DisplayEventSkillValue
  ): void {
    if (value === INCONSISTENT_SKILL_VALUE) {
      return;
    }

    const normalizedValue = this.normalizeSkillValue(value);
    this.collector.eventSkills = { ...(this.collector.eventSkills ?? {}) };
    for (const code of displaySkill.codes) {
      this.collector.eventSkills[code] = normalizedValue;
    }

    this.emitChange();
  }

  trackByLabel(_: number, displaySkill: DisplayEventSkill): string {
    return displaySkill.label;
  }

  private buildDisplayEventSkills(): DisplayEventSkill[] {
    if (this.config?.eventSkillGroups?.length) {
      return this.config.eventSkillGroups
        .map((group) => ({
          label: group.label,
          codes: [...(group.codes ?? [])]
        }))
        .filter((group) => group.label && group.codes.length > 0);
    }

    return (this.config?.eventSkillCodes ?? []).map((eventSkillCode) => ({
      label: eventSkillCode,
      codes: [eventSkillCode]
    }));
  }

  private warnOnInconsistentMergedSkills(): void {
    if (!this.collector || !this.config) {
      return;
    }

    const inconsistentGroups = this.displayEventSkills
      .filter(
        (displaySkill) =>
          displaySkill.codes.length > 1 && this.isInconsistent(displaySkill)
      )
      .map((displaySkill) => displaySkill.label);

    if (!inconsistentGroups.length) {
      this.lastInconsistentAlertKey = null;
      return;
    }

    const collectorKey = this.collector.auth0Id ?? 'unknown';
    const alertKey = `${collectorKey}|${inconsistentGroups.join(',')}`;
    if (alertKey === this.lastInconsistentAlertKey) {
      return;
    }

    this.lastInconsistentAlertKey = alertKey;
    this.alertService.showWarning(
      `Collector has inconsistent split event skill values for ${inconsistentGroups.join(', ')}. Re-save the merged skill to align them.`
    );
  }

  private getSkillValues(codes: string[]): EventSkillValue[] {
    const values = new Set<EventSkillValue>();

    for (const code of codes) {
      values.add(this.normalizeSkillValue(this.collector?.eventSkills?.[code]));
    }

    return [...values];
  }

  private normalizeSkillValue(value: string | null | undefined): EventSkillValue {
    return value &&
      this.config?.eventSkillOptions?.includes(value) &&
      EVENT_SKILL_VALUES.includes(value as EventSkillValue)
      ? (value as EventSkillValue)
      : 'NONE';
  }
}
